import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import TableV3 from "src/components/atoms/TableV3/TableV3";
import { expandJSON } from "src/helpers/helper";
import useLazyFetch from "src/hooks/useLazyFetch";
import AddEditDevice from "./contents/AddEditDevice";
import DeleteDevice from "./contents/DeleteDevice";
import PinDevice from "./contents/PinDevice";

interface IProps {
  initialValues: {
    id: string | number;
    name: string;
    device_id: string;
  };
  onClose: () => void;
}

const DeviceList = ({ initialValues, onClose }: IProps) => {
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [editModal, setEditModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [deviceModal, setDeviceModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [onDeviceList, { loading, data }] = useLazyFetch({
    url: "https://api.automa.id/api/iot/lock/bridges/devices",
    method: "POST",
  });

  const [oneTime, setOneTime] = useState(true);
  const [loadContent, setLoadContent] = useState(false);
  const [sort, setSort] = useState<
    {
      by: string;
      order: "desc" | "asc";
    }[]
  >([]);
  const [filter, setFilter] = useState([]);
  const [page, setPage] = useState({
    of: 1,
    size: 20,
  });

  const convertToQuery = ({
    page,
    sort,
    filter,
    show = [],
  }: {
    page: { of: number; size: number };
    sort: { by: string; order: "asc" | "desc" }[];
    filter: any;
    show?: string[];
  }) => {
    let newFilter = {};
    Object.keys(filter).forEach((item) => {
      newFilter = { ...newFilter, [item]: encodeURI(`%${filter[item]}%`) };
    });

    const query = expandJSON({ page, sort, filter: newFilter, show })
      .map((item) => `${item.label}=${item.value}`)
      .join("&");

    return "?" + query;
  };

  useEffect(() => {
    if (oneTime) {
      setOneTime(false);
      setLoadContent(true);
    }
  }, [oneTime]);
  useEffect(() => {
    if (loadContent) {
      setLoadContent(false);
      onDeviceList({
        path:
          "/list/" +
          initialValues.id +
          convertToQuery({
            page: page,
            sort:
              sort.length === 0
                ? [
                    {
                      by: "created_on",
                      order: "desc",
                    },
                  ]
                : sort,
            filter: filter,
          }),
      });
    }
  }, [filter, initialValues.id, loadContent, onDeviceList, page, sort]);
  return (
    <>
      <Button color="success" type="button" onClick={() => setAddModal(true)}>
        Add Device
      </Button>
      <TableV3
        sort={sort}
        onSort={(s) => {
          setSort([...s]);
          setPage({ ...page, of: 1 });
          setLoadContent(true);
        }}
        page={page}
        totalPage={
          Math.ceil(((!!data ? data.result.total : 1) ?? 1) / page.size) ?? 1
        }
        onPage={(page) => {
          setPage(page);
          setLoadContent(true);
        }}
        filter={filter}
        onFilter={(f) => {
          setFilter(f);
          setPage({ ...page, of: 1 });
          setLoadContent(true);
        }}
        data={!!data ? data.result.data : []}
        columns={[
          {
            name: "Name",
            field: "name",
            sortable: true,
            searchable: true,
          },
          {
            name: "Device ID",
            field: "device_id",
            sortable: true,
            searchable: true,
          },
          {
            name: "Created On",
            field: "created_on",
            sortable: true,
            searchable: true,
          },
          {
            name: "Modified On",
            field: "modified_on",
            sortable: true,
            searchable: true,
          },
          {
            name: "Action",
            field: "modified_on",
            render: (cell, row) => {
              return (
                <>
                  <Button
                    color="primary"
                    type="button"
                    onClick={() => setDeviceModal(row)}
                  >
                    Set PIN
                  </Button>
                  <Button
                    color="warning"
                    type="button"
                    onClick={() => setEditModal(row)}
                  >
                    Edit
                  </Button>
                  <Button
                    color="danger"
                    type="button"
                    onClick={() => setDeleteModal(row)}
                  >
                    Delete
                  </Button>
                </>
              );
            },
          },
        ]}
        loading={loading}
      />

      <Modal isOpen={addModal} toggle={() => setAddModal(!addModal)}>
        <ModalHeader>Add Device</ModalHeader>
        <ModalBody>
          {!!addModal ? (
            <AddEditDevice
              bridgeId={initialValues.id}
              onReload={() => setLoadContent(true)}
              onClose={() => setAddModal(false)}
            />
          ) : null}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!editModal} toggle={() => setEditModal(null)}>
        <ModalHeader>
          Edit Device {!!editModal ? editModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!editModal ? (
            <AddEditDevice
              isEdit
              initialValues={editModal}
              onReload={() => setLoadContent(true)}
              onClose={() => setEditModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
      <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)}>
        <ModalHeader>
          Delete {!!deleteModal ? deleteModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!deleteModal ? (
            <DeleteDevice
              initialValues={deleteModal}
              onReload={() => setLoadContent(true)}
              onClose={() => setDeleteModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
      <Modal isOpen={!!deviceModal} toggle={() => setDeviceModal(null)}>
        <ModalHeader>
          Device {!!deviceModal ? deviceModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!deviceModal ? (
            <PinDevice
              initialValues={deviceModal}
              onClose={() => setDeviceModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>

      <div className="d-flex justify-content-end w-100">
        <Button
          className="w-25 me-2"
          color="secondary ms-2"
          onClick={() => onClose()}
        >
          Close
        </Button>
      </div>
    </>
  );
};

export default DeviceList;
